// API-key lifecycle (custom /api/cloud/auth/api-keys routes). Create returns the
// plaintext token exactly once; list/revoke never expose it. `exchange` is the
// anonymous CLI entry that swaps a key for a short-lived auth JWT — the UI rarely
// needs it, but it is typed here for completeness.

import { getJSON, sendJSON, request } from "./http.js";

const BASE = "/api/cloud/auth/api-keys";

/** KeyKind separates full-authority keys from scoped fine-grained tokens. */
export type KeyKind = "general" | "scoped";

/**
 * Feature is one grantable slice of the resource API for fine-grained tokens.
 * The ids must match the Go vocabulary (internal/access) exactly — the backend
 * rejects anything else at create time.
 */
export type Feature =
  | "workspaces"
  | "builds"
  | "gitsync"
  | "secrets"
  | "render"
  | "teams"
  | "invites"
  | "audit"
  | "usage"
  | "preview"
  | "live"
  | "git"
  | "data"
  | "deployments";

/** FEATURES lists every feature in canonical order, with the UI copy for the
 * fine-grained create form's permission grid. */
export const FEATURES: ReadonlyArray<{ id: Feature; label: string; hint: string }> = [
  { id: "workspaces", label: "Workspaces", hint: "Create, list and delete workspaces and manage their files" },
  { id: "builds", label: "Builds", hint: "Run builds and lints, read results, files and logs" },
  { id: "gitsync", label: "Git sync", hint: "Set up and start git sync" },
  { id: "secrets", label: "Secrets", hint: "Manage team and workspace secrets" },
  { id: "render", label: "Render", hint: "Render manifests into reports" },
  { id: "teams", label: "Teams", hint: "Create and list teams, manage members" },
  { id: "invites", label: "Invitations", hint: "Create and manage team invitations" },
  { id: "audit", label: "Audit log", hint: "Read audit events" },
  { id: "usage", label: "Usage", hint: "Read usage and metering data" },
  { id: "preview", label: "Preview", hint: "Create preview tokens and open previews" },
  { id: "live", label: "Live", hint: "Publish and stop live reports, manage their viewer links" },
  { id: "git", label: "Git", hint: "Clone and push one repository over HTTPS" },
  { id: "data", label: "Data", hint: "Upload and manage data drops" },
  { id: "deployments", label: "Deployments", hint: "Manage deployments and their editions" },
];

/** GitAccess is what a repository key may do over git: clone only, or also push. */
export type GitAccess = "read" | "rw";

/**
 * KeyScope narrows a scoped token: which team (empty = all the owner's teams),
 * which workspace or repository within it (empty = the whole team), and the
 * granted features.
 */
export interface KeyScope {
  /** Team record id; omitted/empty = all teams. */
  team?: string;
  /** Workspace record id; omitted/empty = the whole team. Requires `team`. */
  workspace?: string;
  /** Repository record id: the repository and its branch workspaces. Requires
   * `team`; excludes `workspace`. The `git` feature requires it. */
  repository?: string;
  /** What `git` may do; only with the `git` feature. */
  gitAccess?: GitAccess;
  features: Feature[];
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  /** RFC3339-ish timestamp strings; empty string when never used / no expiry. */
  created: string;
  lastUsed: string;
  expires: string;
  /** Key kind; absent/undefined on older responses means "general". */
  kind?: KeyKind;
  /** Present on scoped (fine-grained) tokens only. */
  scope?: KeyScope;
}

/** The one-time create response: `token` is the plaintext, shown once. */
export interface ApiKeyCreated {
  id: string;
  name: string;
  token: string;
  prefix: string;
  created: string;
  expires: string;
  /** Key kind; absent/undefined on older responses means "general". */
  kind?: KeyKind;
  /** Present on scoped (fine-grained) tokens only. */
  scope?: KeyScope;
}

export interface CreateKeyInput {
  name: string;
  /** Lifetime in seconds; 0 or omitted = never expires. */
  expiresIn?: number;
  /** Kind to mint; omitted = "general". */
  kind?: KeyKind;
  /** Required (with ≥1 feature) when kind is "scoped"; omit otherwise. */
  scope?: KeyScope;
  /** Single-use step-up verification grant (see api/stepup.ts). The server
   * refuses to mint a key without a valid one. */
  verification?: string;
}

/** listApiKeys returns the caller's keys, newest first. */
export async function listApiKeys(): Promise<ApiKey[]> {
  const { items } = await getJSON<{ items: ApiKey[] }>(BASE);
  return items;
}

/** createApiKey mints a key and returns the plaintext token (shown once).
 * `kind`/`scope` are undefined for general keys, so JSON.stringify drops them
 * and the request body stays identical to the pre-scoped-token shape. */
export function createApiKey(input: CreateKeyInput): Promise<ApiKeyCreated> {
  return sendJSON<ApiKeyCreated>(BASE, "POST", {
    name: input.name,
    expiresIn: input.expiresIn ?? 0,
    kind: input.kind,
    scope: input.scope,
    verification: input.verification,
  });
}

/** revokeApiKey deletes one of the caller's keys. */
export async function revokeApiKey(id: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface ExchangeResult {
  token: string;
  expires: string;
  id: string;
}

/** exchangeApiKey swaps a plaintext key for a short-lived auth JWT (anonymous). */
export function exchangeApiKey(token: string): Promise<ExchangeResult> {
  return sendJSON<ExchangeResult>(`${BASE}/exchange`, "POST", { token });
}
